import { observer } from 'mobx-react-lite';
import { default as DurationDropdown, DurationDayLength } from './DurationDropdown';
import DateSelectorMode, {
    SINGLE_LINE_SAME_DAY,
    MULTILINE_WITH_TIME,
    SINGLE_LINE_DURATION,
    MULTILINE_DURATION,
    MULTILINE,
    MULTILINE_WITH_TIMEZONES,
    SINGLE_LINE_ALL_DAY,
} from '../types/DateSelectorMode';
import getDurationInDays from '../utils/getDurationInDays';
import getInitialSelectorMode from '../utils/getInitialSelectorMode';
import updateSelectorMode from '../utils/updateSelectorMode';
import { ThemeProvider } from '@fluentui/react/lib/utilities/ThemeProvider';
import { DefaultButton } from '@fluentui/react/lib/Button';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import generateDropdownColor from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateDropdownColor';
import { DropdownDatePicker } from 'owa-datepicker';
import { isEqual, isSameDay, OwaDate } from 'owa-datetime';
import getDatesOnDurationChanged from 'owa-datetime-utils/lib/dateSelector/getDatesOnDurationChanged';
import getDatesOnEndDateChanged from 'owa-datetime-utils/lib/dateSelector/getDatesOnEndDateChanged';
import getDatesOnEndTimeChanged from 'owa-datetime-utils/lib/dateSelector/getDatesOnEndTimeChanged';
import getDatesOnEndTimeZoneChanged from 'owa-datetime-utils/lib/dateSelector/getDatesOnEndTimeZoneChanged';
import getDatesOnStartDateChanged from 'owa-datetime-utils/lib/dateSelector/getDatesOnStartDateChanged';
import getDatesOnStartTimeChanged from 'owa-datetime-utils/lib/dateSelector/getDatesOnStartTimeChanged';
import getDatesOnStartTimeZoneChanged from 'owa-datetime-utils/lib/dateSelector/getDatesOnStartTimeZoneChanged';
import type { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import loc from 'owa-localize';
import { custom } from 'owa-locstrings/lib/strings/custom.locstring.json';
import { startDateLabel } from 'owa-locstrings/lib/strings/startdatelabel.locstring.json';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { TimeZonePicker } from 'owa-time-zone-picker';
import { useCustomAnimationFrame } from 'owa-react-hooks/lib/useCustomAnimationFrame';
import TimePicker from 'owa-timepicker/lib/components/TimePicker';
import * as React from 'react';
import {
    startTimeLabel,
    startTimeZonePickerAriaLabel,
    toText as toText_1,
    endTimeLabel,
    endDateLabel,
    endTimeZonePickerAriaLabel,
} from './DateSelector.locstring.json';
import {
    getConfiguredEventStartTimeBasedOnPreviousInterval,
    getConfiguredEventEndTimeBasedOnPreviousInterval,
    getConfiguredEventStartTime,
    getConfiguredEventEndTime,
} from 'owa-calendar-cloud-settings-utils';
import {
    getDefaultShouldShortenEventDurationStartLate,
    getDefaultShouldShortenEventDurationEndEarly,
} from 'owa-calendar-cloud-settings';
import { isFeatureEnabled } from 'owa-feature-flags';

import styles from './DateSelector.scss';
import classnamesBind from 'classnames/bind';
let classNames = classnamesBind.bind(styles);

export interface DateSelectorProps {
    start: OwaDate;
    colorScheme?: ColorScheme;
    end: OwaDate;
    isTimePanelViewType?: boolean;
    toTextClassName?: string;
    isAllDay: boolean;
    isNarrow: boolean;
    className?: string;
    datePickerClassName?: string;
    dateRowClassName?: string;
    startEndBlockClassName?: string;
    timeAndDurationColumnClassName?: string;
    durationDropDownClassName?: string;
    timePickerClassName?: string;
    timePickerMultilineClassName?: string;
    hideBorder?: boolean;
    showTimeZones?: boolean;
    onDateTimesChanged: (datesSelected: OwaDateRange) => void;
    onSizeUpdate?: () => void;
    onBeforeTimeZoneSelect: () => void;
    minDate?: OwaDate;
    maxDate?: OwaDate;
    highlightStartDate?: boolean;
    highlightStartTime?: boolean;
    highlightEndDate?: boolean;
    highlightEndTime?: boolean;
    showDateTimeOnly?: boolean;
    disabled?: boolean;
    isEndTimePicker?: boolean;
    isStartTimePicker?: boolean;
    shouldAdjustStartTimeEndTime?: boolean;
    logStartTimeEndTimeChangedCount?: (
        isStartTimePicker: boolean,
        isManuallyChanged: boolean
    ) => void;
    renderAfterDateRow?: (
        timePickerRefTarget: HTMLElement | React.RefObject<Element>
    ) => JSX.Element;
    preferEndDateToBeShown?: boolean;
    preferMultilineAllDay?: boolean;
    preferSingleLineAllDay?: boolean;
}

export default observer(function DateSelector(props: DateSelectorProps) {
    const container = React.useRef<HTMLDivElement>();
    const lastHeight = React.useRef<number>();
    const startTimePickerRef = React.useRef<HTMLDivElement>();
    const endTimePickerRef = React.useRef<HTMLDivElement>();

    const [requestSizeUpdateFrame, , isSizeUpdateRequestActive] = useCustomAnimationFrame();
    React.useEffect(() => {
        if (props.onSizeUpdate && !isSizeUpdateRequestActive()) {
            requestSizeUpdateFrame(() => {
                if (container.current) {
                    let newHeight = container.current.getBoundingClientRect().height;
                    if (lastHeight.current !== undefined && newHeight != lastHeight.current) {
                        props.onSizeUpdate();
                        lastHeight.current = newHeight;
                    }
                    lastHeight.current = container.current.getBoundingClientRect().height;
                }
            });
        }
    });

    let {
        className,
        toTextClassName,
        datePickerClassName,
        dateRowClassName,
        startEndBlockClassName,
        colorScheme,
        hideBorder,
        highlightStartDate,
        highlightStartTime,
        highlightEndDate,
        highlightEndTime,
        showDateTimeOnly,
        minDate,
        maxDate,
        onBeforeTimeZoneSelect,
        timeAndDurationColumnClassName,
        timePickerClassName,
        durationDropDownClassName,
        isAllDay,
        isNarrow,
        timePickerMultilineClassName,
        start,
        end,
        disabled,
        isEndTimePicker,
        isStartTimePicker,
        shouldAdjustStartTimeEndTime,
        renderAfterDateRow,
        preferEndDateToBeShown,
        preferMultilineAllDay,
        preferSingleLineAllDay,
    } = props;

    const [currentSelectorMode, setCurrentSelectorMode] = React.useState(() =>
        getInitialSelectorMode(
            isNarrow,
            isAllDay,
            start,
            end,
            props.showTimeZones,
            preferEndDateToBeShown,
            preferMultilineAllDay,
            preferSingleLineAllDay
        )
    );

    const [startDate, setStartDate] = React.useState(start);
    const [endDate, setEndDate] = React.useState(end);
    const [isAllDayEvent, setIsAllDayEvent] = React.useState(isAllDay);
    const [isNarrowWindow, setIsNarrowWindow] = React.useState(isNarrow);
    const [showEndTime, setShowEndTime] = React.useState(
        currentSelectorMode == MULTILINE_WITH_TIME ||
            currentSelectorMode == MULTILINE_WITH_TIMEZONES
    );
    const [showDuration, setShowDuration] = React.useState(
        currentSelectorMode == SINGLE_LINE_DURATION || currentSelectorMode == MULTILINE_DURATION
    );
    const [showTimeOrDuration, setShowTimeOrDuration] = React.useState(
        currentSelectorMode != MULTILINE && currentSelectorMode != SINGLE_LINE_ALL_DAY
    );
    const [showEndDateInFirstRow, setShowEndDateInFirstRow] = React.useState(
        currentSelectorMode == SINGLE_LINE_ALL_DAY
    );
    const [showEndDateInSecondRow, setShowEndDateInSecondRow] = React.useState(
        currentSelectorMode == MULTILINE_WITH_TIME ||
            currentSelectorMode == MULTILINE_DURATION ||
            currentSelectorMode == MULTILINE ||
            currentSelectorMode == MULTILINE_WITH_TIMEZONES
    );
    const [isCustomEvent, setIsCustomEvent] = React.useState(false); // all new events default to isCustom false
    /**
     * This property is used to ensure that the end time picker enables the focus when it is mounted. This time picker needs
     * focus because, in IE, selecting the "custom" for the end time set focus outside of the compose form which caused it to close.
     * The time picker must focus on mount when custom is selected, otherwise it should not.
     */
    const [shouldFocusEndTimePickerOnMount, setShouldFocusEndTimePickerOnMount] = React.useState(
        false
    );
    const [showTimeZones, setShowTimeZones] = React.useState(
        currentSelectorMode == MULTILINE_WITH_TIMEZONES
    );
    const [wasEndTimeZoneSetManually, setWasEndTimeZoneSetManually] = React.useState(false);
    const [wasEndTimeSetManually, setWasEndTimeSetManually] = React.useState(false);
    const [isSpeedyMeetingStartTimeOption, setIsSpeedyMeetingStartTimeOption] = React.useState(
        getDefaultShouldShortenEventDurationStartLate()
    );
    const [isSpeedyMeetingEndTimeOption, setIsSpeedyMeetingEndTimeOption] = React.useState(
        getDefaultShouldShortenEventDurationEndEarly()
    );

    React.useLayoutEffect(() => {
        const startChanged = !isEqual(startDate, props.start);
        const endChanged = !isEqual(endDate, props.end);
        const startOrEndChanged = startChanged || endChanged;
        const isAllDayChanged = isAllDayEvent != props.isAllDay;
        const showTimeZonesChanged =
            props.showTimeZones != undefined && showTimeZones != props.showTimeZones;
        const isNarrowChange = isNarrowWindow != props.isNarrow;

        if (startOrEndChanged) {
            setStartDate(props.start);
            setEndDate(props.end);
        }

        setIsAllDayEvent(props.isAllDay);
        setShowTimeZones(props.showTimeZones ?? false);
        setIsNarrowWindow(props.isNarrow);

        if (showTimeZonesChanged) {
            setWasEndTimeZoneSetManually(false);
            updateSelectorModeOnShowTimeZonesChanged(props.showTimeZones);
        } else if (isAllDayChanged && startOrEndChanged) {
            startOrEndAndDurationChanged(props.isAllDay, props.start, props.end);
        } else if (isAllDayChanged) {
            updateSelectorModeAllDayChanged(props.isAllDay);
        } else if (startOrEndChanged) {
            onStartOrEndChanged(startDate, endDate);
        } else if (isNarrowChange) {
            updateSelectorModeOnIsNarrowChanged(props.isNarrow);
        }
    }, [props.start, props.end, props.isAllDay, props.showTimeZones, props.isNarrow]);

    React.useLayoutEffect(() => {
        updateSelectorModeDurationChanged(isCustomEvent);
    }, [isCustomEvent]);

    /**
     * Sets the current state of the component
     */
    const setStateBasedOnMode = (currentSelectorMode: DateSelectorMode) => {
        setShowTimeOrDuration(
            currentSelectorMode != MULTILINE && currentSelectorMode != SINGLE_LINE_ALL_DAY
        );
        setShowEndTime(
            currentSelectorMode == MULTILINE_WITH_TIME ||
                currentSelectorMode == MULTILINE_WITH_TIMEZONES
        );
        setShowEndDateInFirstRow(currentSelectorMode == SINGLE_LINE_ALL_DAY);
        setShowEndDateInSecondRow(
            currentSelectorMode == MULTILINE_WITH_TIME ||
                currentSelectorMode == MULTILINE_DURATION ||
                currentSelectorMode == MULTILINE ||
                currentSelectorMode == MULTILINE_WITH_TIMEZONES
        );
        setShowDuration(
            currentSelectorMode == SINGLE_LINE_DURATION || currentSelectorMode == MULTILINE_DURATION
        );
        setShowTimeZones(currentSelectorMode == MULTILINE_WITH_TIMEZONES);
        setIsCustomEvent(
            currentSelectorMode == MULTILINE || currentSelectorMode == MULTILINE_WITH_TIME
        );
        setCurrentSelectorMode(currentSelectorMode);
    };
    const updateShouldFocusEndTimePickerOnMount = (shouldFocus: boolean) => {
        setShouldFocusEndTimePickerOnMount(shouldFocus);
    };
    /**
     * Updates the currentSelectorMode when the start or end date is changed
     */
    const onStartOrEndChanged = (start: OwaDate, end: OwaDate) => {
        updateSelectorModeDurationChanged(false, getDurationInDays(start, end));
    };
    /**
     * Updates the currentSelectorMode when the (start or end date) and isAllDay is changed
     */
    const startOrEndAndDurationChanged = (isAllDay: boolean, start: OwaDate, end: OwaDate) => {
        updateSelectorModeAllDayAndDurationChanged(isAllDay, false, getDurationInDays(start, end));
    };
    /**
     * Callback for when 'Custom' is selected from time picker
     */
    const selectCustomDate = () => {
        setIsCustomEvent(true);
        updateShouldFocusEndTimePickerOnMount(true);
    };

    const lowerContent = () => {
        return (
            <DefaultButton
                className={styles.lowerButton}
                data-automation-id="customButton"
                text={loc(custom)}
                ariaLabel={loc(custom)}
                onClick={selectCustomDate}
            />
        );
    };
    /**
     * Method to set the next selectorMode state when the duration is changed or the end time picker is set to custom
     * @param isCustom set this to true when the new mode is being set by a custom selection from the duration or time picker
     * @param durationInDays optional. The updated duration in days of the event. If the duration update has not trigger the function leave it null
     */
    const updateSelectorModeDurationChanged = (isCustom: boolean, durationInDays?: number) => {
        let { isNarrow } = props;
        if (durationInDays == null) {
            if (isCustom) {
                durationInDays = DurationDayLength.Custom;
            } else {
                durationInDays = getDurationInDays(startDate, endDate);
            }
        }
        let newSelectorMode: DateSelectorMode = updateSelectorMode(
            isNarrow,
            currentSelectorMode,
            isCustom,
            durationInDays,
            showTimeZones,
            preferEndDateToBeShown,
            preferMultilineAllDay,
            preferSingleLineAllDay,
            null
        );
        if (newSelectorMode) {
            setStateBasedOnMode(newSelectorMode);
        }
    };
    /**
     * Method to set the next selectorMode state when isAllDay value is changed
     * @param isAllDay The new isAllDay value
     */
    const updateSelectorModeAllDayChanged = (isAllDay: boolean) => {
        let { isNarrow } = props;
        let newSelectorMode: DateSelectorMode = updateSelectorMode(
            isNarrow,
            currentSelectorMode,
            false,
            getDurationInDays(startDate, endDate),
            showTimeZones,
            preferEndDateToBeShown,
            preferMultilineAllDay,
            preferSingleLineAllDay,
            isAllDay
        );
        if (newSelectorMode) {
            setStateBasedOnMode(newSelectorMode);
        }
    };
    /**
     * Method to set the next selectorMode state when isAllDay value is changed
     * @param isAllDay The new isAllDay value
     * @param isCustom set this to true when the new mode is being set by a custom selection from the duration or time picker
     * @param durationInDays The updated duration in days of the event.
     */
    const updateSelectorModeAllDayAndDurationChanged = (
        isAllDay: boolean,
        isCustom: boolean,
        durationInDays: number
    ) => {
        let { isNarrow } = props;
        let newSelectorMode: DateSelectorMode = updateSelectorMode(
            isNarrow,
            currentSelectorMode,
            isCustom,
            durationInDays,
            showTimeZones,
            preferEndDateToBeShown,
            preferMultilineAllDay,
            preferSingleLineAllDay,
            isAllDay
        );
        if (newSelectorMode) {
            setStateBasedOnMode(newSelectorMode);
        }
    };
    /**
     * Method to set the next selectorMode state when showTimeZones value is changed
     * @param showTimeZones the new showTimeZones value
     */
    const updateSelectorModeOnShowTimeZonesChanged = (showTimeZones: boolean) => {
        let { isNarrow, isAllDay } = props;
        // Revert to default time zones if they are hidden.
        if (!showTimeZones) {
            updateTimeZonesAndDates(getUserConfiguration().UserOptions.TimeZone, false);
        }
        let newSelectorMode: DateSelectorMode = updateSelectorMode(
            isNarrow,
            currentSelectorMode,
            false,
            getDurationInDays(startDate, endDate),
            showTimeZones,
            preferEndDateToBeShown,
            preferMultilineAllDay,
            preferSingleLineAllDay,
            isAllDay
        );
        if (newSelectorMode) {
            setStateBasedOnMode(newSelectorMode);
        }
    };

    const updateSelectorModeOnIsNarrowChanged = (isNarrow: boolean) => {
        let { isAllDay } = props;
        let newSelectorMode: DateSelectorMode = updateSelectorMode(
            isNarrow,
            currentSelectorMode,
            false,
            getDurationInDays(startDate, endDate),
            showTimeZones,
            preferEndDateToBeShown,
            preferMultilineAllDay,
            preferSingleLineAllDay,
            isAllDay
        );
        if (newSelectorMode) {
            setStateBasedOnMode(newSelectorMode);
        }
    };

    const updateDates = (
        selectedDates: OwaDateRange,
        isStartTime?: boolean,
        isManuallyChanged?: boolean
    ) => {
        if (selectedDates) {
            const { start, end } = selectedDates;
            {
                setStartDate(start);
                setEndDate(end);
            }
            props.onDateTimesChanged(selectedDates);

            if (props.logStartTimeEndTimeChangedCount && isStartTime != undefined) {
                props.logStartTimeEndTimeChangedCount(isStartTime, isManuallyChanged);
            }
        }
    };
    const onStartDateChanged = (newDate: OwaDate) => {
        let selectedDates: OwaDateRange = getDatesOnStartDateChanged(startDate, endDate, newDate);
        updateDates(selectedDates);
    };
    const onEndDateChanged = (newDate: OwaDate) => {
        let selectedDates: OwaDateRange = getDatesOnEndDateChanged(startDate, endDate, newDate);
        updateDates(selectedDates);
    };
    const onStartTimeChanged = (
        date: OwaDate,
        isManuallyChanged: boolean,
        isSpeedyMeetingOption?: boolean
    ) => {
        let selectedDates: OwaDateRange = getDatesOnStartTimeChanged(
            startDate,
            endDate,
            date,
            !wasEndTimeSetManually /* the duration should always be kept if the end time has not set manually */
        );

        const updatedSelectedDates = adjustStartTimeEndTimeWhenStartTimeChanged(
            selectedDates,
            date,
            isManuallyChanged,
            isSpeedyMeetingOption
        );

        updateDates(updatedSelectedDates, true /* isStartTime */, isManuallyChanged);
        setIsSpeedyMeetingStartTimeOption(isSpeedyMeetingOption);
    };
    // Auto-adjust start time and end time if it's necessary when speedy meeting is enabled
    // E.g.1: 12:05 - 12:30, change end time to 1:00 (12:10 - 1:00), then change start time to 12:40 (12:40 - 1:00)
    // Result: auto-updates start time to 12:35 to match the settings (12:35 - 1:00 - shorten by 5 min)
    // E.g.2: 12:00 - 12:25, change end time to 12:50 (12:00 - 12:50), then change start time to 12:30 (12:30 - 12:50)
    // Result: auto-updates end time to 12:55 to match the settings (12:30 - 12:55 - shorten by 5 min)
    // If shortenEventsDurationTimePickerLogic is enable, we don't do auto-adjustments for non-speedy meetings options (limits the adjustments).
    // E.g. start late speedy meeting options: :05/:10/:35/:50
    // E.g. end early speedy meeting options: :20/:25/:35/:40
    // E.g. non-speedy meeting options: :00/:30
    const adjustStartTimeEndTimeWhenStartTimeChanged = (
        selectedDates: OwaDateRange,
        newStart: OwaDate,
        isManuallyChanged: boolean,
        isSpeedyMeetingOption?: boolean
    ): OwaDateRange => {
        // If we don't need to adjust dates, return original dates
        if (!shouldAdjustStartTimeEndTime) {
            return selectedDates;
        }

        // Only auto-adjusts the start time for start late if it is not manually changed by the user
        // And if both the old start time and the new start time are speedy meeting options
        if (
            !isManuallyChanged &&
            isSpeedyMeetingOption &&
            isSpeedyMeetingStartTimeOption &&
            getDefaultShouldShortenEventDurationStartLate()
        ) {
            // Auto-adjust start time based on the previous start time and end time for options ending with :05/:10/:35/:50
            selectedDates.start = getConfiguredEventStartTimeBasedOnPreviousInterval(
                startDate,
                endDate,
                newStart,
                selectedDates.end,
                true /* useNewStartAsPrevious */
            );
        }

        // Auto-adjusts the end time for end early
        if (getDefaultShouldShortenEventDurationEndEarly()) {
            if (isSpeedyMeetingEndTimeOption) {
                // Auto-adjust end time based on the previous start time and end time for options ending with :20/:25/:35/:40
                selectedDates.end = getConfiguredEventEndTimeBasedOnPreviousInterval(
                    startDate,
                    endDate,
                    newStart,
                    selectedDates.end
                );
            } else {
                // Auto-adjust end time for non-speedy meeting options only when the flight is off
                if (!isFeatureEnabled('cal-mf-shortenEventsDurationTimePickerLogic')) {
                    // Auto-adjust end time based on the new start time and end time for options ending with :00/:30
                    selectedDates.end = getConfiguredEventEndTime(newStart, selectedDates.end);
                    setIsSpeedyMeetingEndTimeOption(true);
                }
            }
        }

        return selectedDates;
    };
    const onEndTimeChanged = (
        date: OwaDate,
        isManuallyChanged: boolean,
        isSpeedyMeetingOption?: boolean
    ) => {
        let selectedDates: OwaDateRange = getDatesOnEndTimeChanged(startDate, endDate, date);

        const updatedSelectedDates = adjustStartTimeWhenEndTimeChanged(selectedDates, date);

        updateDates(updatedSelectedDates, false /* isStartTime */, isManuallyChanged);
        setWasEndTimeSetManually(true);
        setIsSpeedyMeetingEndTimeOption(isSpeedyMeetingOption);
    };
    // Auto-adjust start time if it's necessary when speedy meeting is enabled
    // E.g.1: 12:05 - 12:30, change end time to 1:00 (12:05 - 1:00)
    // Result: auto-updates start time to 12:10 to match the settings (12:10 - 1:00 - shorten by 10 min)
    // E.g.2: 12:10 - 1:00, change end time to 12:30 (12:10 - 12:30)
    // Result: auto-updates start time to 12:05 to match the settings (12:05 - 12:30 - shorten by 5 min)
    // If shortenEventsDurationTimePickerLogic is enable, we don't do auto-adjustments for non-speedy meetings options (limits the adjustments).
    // E.g. start late speedy meeting options: :05/:10/:35/:50
    // E.g. end early speedy meeting options: :20/:25/:35/:40
    // E.g. non-speedy meeting options: :00/:30
    const adjustStartTimeWhenEndTimeChanged = (
        selectedDates: OwaDateRange,
        newEnd: OwaDate
    ): OwaDateRange => {
        // If we don't need to adjust dates, return original dates
        if (!shouldAdjustStartTimeEndTime) {
            return selectedDates;
        }

        // Auto-adjusts the start time for start late
        if (getDefaultShouldShortenEventDurationStartLate()) {
            if (isSpeedyMeetingStartTimeOption) {
                // Auto-adjust start time based on the previous start time and end time for options ending with :05/:10/:35/:50
                selectedDates.start = getConfiguredEventStartTimeBasedOnPreviousInterval(
                    startDate,
                    endDate,
                    startDate,
                    newEnd,
                    false /* useNewStartAsPrevious */,
                    true /* ignoreZeroDuration */
                );
            } else {
                // Auto-adjust start time for non-speedy meeting options only when the flight is off
                if (!isFeatureEnabled('cal-mf-shortenEventsDurationTimePickerLogic')) {
                    // Auto-adjust start time based on the new start time and end time for options ending with :00/:30
                    selectedDates.start = getConfiguredEventStartTime(startDate, newEnd);
                    setIsSpeedyMeetingStartTimeOption(true);
                }
            }
        }

        return selectedDates;
    };
    /**
     * Updates the end date based on the duration given
     */
    const updateDuration = (durationInDays: number) => {
        updateSelectorModeDurationChanged(false, durationInDays);
        let selectedDates: OwaDateRange = getDatesOnDurationChanged(startDate, durationInDays);
        updateDates(selectedDates);
    };
    const onCustomDuration = () => {
        setIsCustomEvent(true);
    };
    const onEndTimeTimePickerBlur = () => updateSelectorModeDurationChanged(isCustomEvent);
    const updateTimeZonesAndDates = (newTimeZone: string, preserveEndTimeZone: boolean) => {
        let selectedDates: OwaDateRange = getDatesOnStartTimeZoneChanged(
            startDate,
            endDate,
            newTimeZone,
            preserveEndTimeZone ? endDate.tz : newTimeZone
        );
        updateDates(selectedDates);
    };
    const onStartTimeZoneChanged = (newTimeZone: string) => {
        updateTimeZonesAndDates(newTimeZone, wasEndTimeZoneSetManually);
    };
    const onEndTimeZoneChanged = (newTimeZone: string) => {
        let selectedDates: OwaDateRange = getDatesOnEndTimeZoneChanged(
            startDate,
            endDate,
            newTimeZone
        );
        updateDates(selectedDates);
        setWasEndTimeZoneSetManually(true);
    };
    let dropdownColorScheme = generateDropdownColor(colorScheme);
    let minEndTime = startDate;
    let theme = themeFromColorScheme(colorScheme);
    let highlightBackgroundStyle = { background: colorScheme.calLighterAlt };
    const timePickerClassNames = classNames(timePickerClassName, styles.timePicker, {
        [styles.timePickerDefaultBorder]: !hideBorder,
        [styles.timePickerHideBorder]: hideBorder,
        [timePickerMultilineClassName]: currentSelectorMode === MULTILINE_WITH_TIME,
    });
    const dateRowClassNames = classNames(dateRowClassName, styles.dateRow);
    const timeAndDurationColumnClassNames = classNames(
        timeAndDurationColumnClassName,
        styles.timeAndDurationColumn
    );
    const timePickerComboBoxRootStyle = classNames(styles.timePickerRootStyle, {
        [timePickerMultilineClassName]: currentSelectorMode === MULTILINE_WITH_TIME,
    });

    /**
     * Returns the time picker ref to be used as target for the speedy meetings - user education
     */
    const getTimePickerRef = () => {
        if (getDefaultShouldShortenEventDurationStartLate()) {
            return startTimePickerRef;
        }
        if (getDefaultShouldShortenEventDurationEndEarly()) {
            return endTimePickerRef;
        }
        return null;
    };

    return (
        <ThemeProvider
            theme={theme}
            ref={container}
            className={classNames(className, styles.dateSelectorContainer)}>
            <div className={dateRowClassNames}>
                <div
                    className={classNames(styles.datePicker, {
                        [styles.datePickerDefaultBorder]: !hideBorder,
                        [styles.datePickerHideBorder]: hideBorder,
                    })}
                    style={highlightStartDate ? highlightBackgroundStyle : null}>
                    <DropdownDatePicker
                        className={datePickerClassName}
                        value={startDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        onSelectDate={onStartDateChanged}
                        ariaLabel={loc(startDateLabel)}
                        isRequired={true}
                        disabled={disabled}
                    />
                </div>
                {showEndDateInFirstRow && (
                    <>
                        <div className={classNames(styles.toText)}>{loc(toText_1)}</div>
                        <div
                            className={classNames(styles.datePicker, {
                                [styles.datePickerDefaultBorder]: !hideBorder,
                                [styles.datePickerHideBorder]: hideBorder,
                            })}
                            style={highlightEndDate ? highlightBackgroundStyle : null}>
                            <DropdownDatePicker
                                className={datePickerClassName}
                                value={endDate}
                                onSelectDate={onEndDateChanged}
                                ariaLabel={loc(endDateLabel)}
                                minDate={minEndTime}
                                isRequired={true}
                                disabled={disabled}
                            />
                        </div>
                    </>
                )}
                {showTimeOrDuration && !showDuration && (
                    <div
                        className={classNames(startEndBlockClassName, styles.startAndEndTimeLine, {
                            [styles.topRowBottomMargin]: showEndTime,
                            [styles.startAndEndTimeLineLength]: showEndTime,
                        })}>
                        <div
                            ref={startTimePickerRef}
                            style={highlightStartTime ? highlightBackgroundStyle : null}>
                            <TimePicker
                                timePickerColorScheme={dropdownColorScheme}
                                className={timePickerClassNames}
                                selectedTime={startDate}
                                onChanged={onStartTimeChanged}
                                ariaLabel={loc(startTimeLabel)}
                                timePickerComboBoxRootStyle={timePickerComboBoxRootStyle}
                                readOnly={disabled}
                                isStartTimePicker={isStartTimePicker}
                                eventEndTime={endDate}
                                shouldAdjustStartTimeEndTime={shouldAdjustStartTimeEndTime}
                            />
                        </div>
                        {showTimeZones && (
                            <div className={classNames(styles.timeZoneDropdown)}>
                                <TimeZonePicker
                                    value={startDate.tz}
                                    onChange={onStartTimeZoneChanged}
                                    onBeforeOptionSelect={onBeforeTimeZoneSelect}
                                    borderless={hideBorder}
                                    underlined={!hideBorder}
                                    ariaLabel={loc(startTimeZonePickerAriaLabel)}
                                    disabled={disabled}
                                />
                            </div>
                        )}
                        {!showEndDateInSecondRow && !showDateTimeOnly && (
                            <>
                                <div className={classNames(toTextClassName, styles.toText)}>
                                    {loc(toText_1)}
                                </div>
                                <div
                                    ref={endTimePickerRef}
                                    style={highlightEndTime ? highlightBackgroundStyle : null}>
                                    <TimePicker
                                        timePickerColorScheme={dropdownColorScheme}
                                        className={timePickerClassNames}
                                        selectedTime={endDate}
                                        onChanged={onEndTimeChanged}
                                        durationClassName={styles.formatDuration}
                                        startTime={minEndTime}
                                        ariaLabel={loc(endTimeLabel)}
                                        onRenderLowerContent={lowerContent}
                                        timePickerComboBoxRootStyle={timePickerComboBoxRootStyle}
                                        readOnly={disabled}
                                        isEndTimePicker={isEndTimePicker}
                                        eventStartTime={startDate}
                                        shouldAdjustStartTimeEndTime={shouldAdjustStartTimeEndTime}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {showDuration && (
                    <DurationDropdown
                        className={classNames(durationDropDownClassName, styles.durationDropdown, {
                            hideBorder: hideBorder,
                        })}
                        colorScheme={colorScheme}
                        onDurationChanged={updateDuration}
                        onCustomSelected={onCustomDuration}
                        selectedDurationInDays={getDurationInDays(startDate, endDate)}
                        disabled={disabled}
                    />
                )}
            </div>

            <div
                className={classNames(dateRowClassNames, {
                    [styles.bottomPicker]:
                        showEndDateInSecondRow ||
                        (showTimeOrDuration && (showEndTime || showTimeZones)),
                })}>
                {showEndDateInSecondRow && (
                    <div
                        className={classNames(
                            isAllDay ? styles.allDaySecondDatePicker : null,
                            styles.datePicker,
                            {
                                [styles.datePickerDefaultBorder]: !hideBorder,
                                [styles.datePickerHideBorder]: hideBorder,
                            }
                        )}
                        style={highlightEndDate ? highlightBackgroundStyle : null}>
                        <DropdownDatePicker
                            className={datePickerClassName}
                            value={endDate}
                            onSelectDate={onEndDateChanged}
                            ariaLabel={loc(endDateLabel)}
                            minDate={minEndTime}
                            isRequired={true}
                            disabled={disabled}
                        />
                    </div>
                )}
                {showTimeOrDuration && (
                    <div
                        className={classNames(timeAndDurationColumnClassNames, {
                            // When the selectorMode is singleLineSameDay, the width of the timeOrDurationColumn
                            // needs to be larger (since it has to fit the two end time picker and start picker side by side)
                            [styles.timeOrDurationWide]:
                                currentSelectorMode == SINGLE_LINE_SAME_DAY,
                        })}>
                        {showEndTime && (
                            <div
                                ref={endTimePickerRef}
                                style={highlightEndTime ? highlightBackgroundStyle : null}>
                                <TimePicker
                                    timePickerColorScheme={dropdownColorScheme}
                                    className={timePickerClassNames}
                                    selectedTime={endDate}
                                    onChanged={onEndTimeChanged}
                                    durationClassName={styles.formatDuration}
                                    startTime={isSameDay(minEndTime, endDate) ? minEndTime : null}
                                    ariaLabel={loc(endTimeLabel)}
                                    setFocusOnMount={shouldFocusEndTimePickerOnMount}
                                    onBlur={onEndTimeTimePickerBlur}
                                    timePickerComboBoxRootStyle={timePickerComboBoxRootStyle}
                                    readOnly={disabled}
                                    isEndTimePicker={isEndTimePicker}
                                    eventStartTime={startDate}
                                    shouldAdjustStartTimeEndTime={shouldAdjustStartTimeEndTime}
                                />
                            </div>
                        )}

                        {showTimeZones && (
                            <div className={classNames(styles.timeZoneDropdown)}>
                                <TimeZonePicker
                                    value={endDate.tz}
                                    onChange={onEndTimeZoneChanged}
                                    onBeforeOptionSelect={onBeforeTimeZoneSelect}
                                    borderless={hideBorder}
                                    underlined={!hideBorder}
                                    ariaLabel={loc(endTimeZonePickerAriaLabel)}
                                    disabled={disabled}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
            {getTimePickerRef() && renderAfterDateRow?.(getTimePickerRef())}
        </ThemeProvider>
    );
});
